<script lang="ts">
  /**
   * The conversation view: a list of threads and the open one, in one component.
   *
   * ONE LAYOUT, TWO SHAPES, DECIDED BY THE CONTAINER
   * ------------------------------------------------
   * Narrow, it is what the side panel has today: the list, and opening a thread
   * pushes over it with a Back button. Wide, it is a mail client: the list on the
   * left, the open thread on the right, no navigation between them.
   *
   * The breakpoint is measured on THIS ELEMENT with a `ResizeObserver`, not with a
   * media query, and that is not a preference. `chrome.sidePanel` exposes no width —
   * the owner drags the panel and Chrome remembers — and the web app renders this
   * inside a page whose available width has nothing to do with the viewport's. A
   * media query would ask the wrong question on both surfaces: a wide monitor with a
   * narrow panel would get two panes in 340px, and a narrow window with the web app
   * maximised would get one pane in 900px.
   *
   * So the same component is a panel and a mail client, and neither app knows which
   * it asked for.
   *
   * WHY THE THREAD STAYS MOUNTED IN BOTH SHAPES. Crossing the breakpoint mid-read —
   * dragging the panel wider — must not reload the conversation or lose the scroll
   * position. The two shapes differ in POSITION, not in what exists, so the same
   * thread instance is placed differently rather than torn down and rebuilt.
   */
  import { onMount } from 'svelte';
  import ConversationList from './ConversationList.svelte';
  import ConversationThread from './ConversationThread.svelte';
  import type { ConversationView, DmMessageView } from './types';

  let {
    conversations,
    openId = $bindable(null),
    query = $bindable(''),
    searchPlaceholder = 'Search conversations',
    messages,
    hasOlder = false,
    onLoadOlder,
    loading = false,
    threadLoading = false,
    error = null,
    threadError = null,
    /** See `MessageBubble.timestamp`. Passed straight through; the density beside it
     *  is measured, but whether an instant matters is the surface's own question. */
    timestamp = 'relative',
    /** Width at which the two-pane layout earns its place. Below it the list and the
     *  thread would each be too narrow to read; the number is where a 320px list
     *  plus a readable thread stops fitting. */
    twoPaneAt = 640,
    /** Shared clock. Hosts that tick it get relative times that stay honest
     *  without every row owning a timer. */
    now = Date.now(),
    stickyTop = null,
    receipt = null,
    header,
    empty,
    composer,
    threadHeader,
  }: {
    conversations: ConversationView[];
    openId?: string | null;
    /**
     * What the reader typed. BOUND, not acted on: this owns the input and nothing
     * else, and the host decides what a query means.
     *
     * That split is the reason both surfaces can share one box. The Helper holds the
     * whole list in memory and narrows it with a `$derived`; the explorer pages a
     * hundred conversations at a time out of six thousand, so it debounces and asks
     * the server. A component that filtered `conversations` itself would be wrong for
     * the second — it would search the page instead of the inbox — and there is no
     * honest way for it to know which case it is in.
     */
    query?: string;
    searchPlaceholder?: string;
    /**
     * Pin the search row to this offset inside the host's scroll container.
     *
     * Same bargain as `<InboxDeck>`'s: the Helper panel is one screen with one
     * scroller, and a search box that scrolls away is one you cannot reach while
     * reading a thread. The explorer scrolls the page behind its own chrome, passes
     * nothing, and keeps today's behaviour.
     */
    stickyTop?: string | null;
    /** Forwarded untouched to `<ConversationThread>` — see its `receipt`. */
    receipt?: 'sent' | 'read' | null;
    /** The OPEN conversation's messages. Owned by the host — see ConversationThread. */
    messages: DmMessageView[];
    hasOlder?: boolean;
    onLoadOlder?: (() => Promise<void>) | undefined;
    loading?: boolean;
    threadLoading?: boolean;
    error?: string | null;
    threadError?: string | null;
    timestamp?: 'relative' | 'exact';
    twoPaneAt?: number;
    now?: number;
    /** Controls that belong BESIDE the search box — a refresh, a filter toggle, an
     *  unread badge. Rendered in the same row, so the toolbar is one line on both
     *  surfaces instead of two stacked ones. */
    header?: import('svelte').Snippet | undefined;
    composer?: import('svelte').Snippet | undefined;
    empty?: import('svelte').Snippet | undefined;
    threadHeader?: import('svelte').Snippet | undefined;
  } = $props();

  let root = $state<HTMLElement | null>(null);
  let width = $state(0);
  const twoPane = $derived(width >= twoPaneAt);

  onMount(() => {
    if (!root) return;
    // Seed from the current size: a ResizeObserver fires on observe, but reading it
    // here means the first paint is already the right shape rather than flashing the
    // narrow one for a frame.
    width = root.getBoundingClientRect().width;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) width = e.contentRect.width;
    });
    ro.observe(root);
    return () => ro.disconnect();
  });

  const open = $derived(conversations.find((c) => c.id === openId) ?? null);

  /**
   * Density, from the SAME measurement that picks the layout.
   *
   * A bubble sized for 340px stretched across a 700px pane reads as a mistake, and
   * the two questions have one answer: below the two-pane threshold this is a side
   * panel, above it this is a mail client. So neither app passes a density and a
   * panel dragged wide gets the roomy treatment on its own.
   */
  const compact = $derived(width > 0 && width < twoPaneAt);
</script>

<div bind:this={root} class="flex min-h-0 flex-col gap-2">
  <!-- One toolbar: the search this component owns, then whatever the host puts beside
       it. Both apps had grown their own search box over their own copy of this list. -->
  <div
    class="flex items-center gap-2 {stickyTop ? 'sticky z-30 bg-base-100 pt-2 pb-2' : ''}"
    style={stickyTop ? `top:${stickyTop}` : undefined}
  >
    <label class="flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-base-300 bg-base-100 px-2 focus-within:border-primary">
      <svg class="h-3.5 w-3.5 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
      <input
        type="search"
        bind:value={query}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        class="w-full bg-transparent text-xs outline-none placeholder:text-base-content/55"
      />
    </label>
    {#if header}{@render header()}{/if}
  </div>

  {#if twoPane}
    <!-- TWO PANES. The list keeps a fixed, readable width and the thread takes the
         rest — a conversation is the thing being read, so it gets the slack. -->
    <div class="flex min-h-0 flex-1 gap-2 max-h-[var(--sg-conv-h,70vh)]">
      <div class="w-[19rem] shrink-0 overflow-y-auto">
        <ConversationList
          {conversations}
          bind:openId
          {loading}
          {error}
          selectable
          {now}
          {empty}
        />
      </div>
      <div class="min-w-0 flex-1">
        {#if open}
          <ConversationThread conversation={open} {messages} {hasOlder} {onLoadOlder} loading={threadLoading} error={threadError} {compact} {timestamp} {now} {receipt} {composer} {threadHeader} />
        {:else}
          <!-- Not an error and not empty: the list has content and nothing is picked
               yet, which is the ordinary resting state of a two-pane view. -->
          <div class="flex h-full min-h-[12rem] items-center justify-center rounded-box border hair bg-base-100">
            <p class="text-xs opacity-55">Pick a conversation</p>
          </div>
        {/if}
      </div>
    </div>
  {:else if open}
    <!-- NARROW, THREAD OPEN. A push-over view with its own way back, because at this
         width the list and the thread cannot both be legible. -->
    <ConversationThread
      conversation={open}
      {messages}
      {hasOlder}
      {onLoadOlder}
      {receipt}
      loading={threadLoading}
      error={threadError}
      {compact}
      {timestamp}
      {now}
      {composer}
      {threadHeader}
      onback={() => (openId = null)}
    />
  {:else}
    <ConversationList {conversations} bind:openId {loading} {error} {now} {empty} />
  {/if}
</div>
